import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Skeleton } from 'antd';
import moment from 'moment';
import styled from 'styled-components';

import {
  IStudentMiniCard,
  sentimentTone,
  useCanOpenStudentCard,
} from 'core/hooks';
import { useAiAgentPermissions } from 'core/hooks/AiAgents/useAiAgentPermissions';
import { formatAmountsDue } from 'core/utils/currencyTotals';
import { LmsBlockBadge } from 'molecules';
import { PATHS } from 'router/config';

import { personName } from 'core/utils/personName';
import { MiniCardAiPortrait } from './MiniCardAiPortrait';
import * as S from './StudentMiniCard.styled';

interface IProps {
  studentId: string;
  card: IStudentMiniCard | null;
  loading: boolean;
  failed: boolean;
  onRetry?: () => void;
}

const MONEY_FMT = new Intl.NumberFormat('pl-PL', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const money = (n: number): string => MONEY_FMT.format(n);
const date = (iso: string): string => moment(iso).format('DD.MM.YYYY');

const STATUS_TONE: Record<string, 'ok' | 'muted' | 'info'> = {
  active: 'ok',
  terminated: 'muted',
  transferred_out: 'info',
};

const STATUS_KEY: Record<string, string> = {
  active: 'pages.communicator.miniCard.status.active',
  terminated: 'pages.communicator.miniCard.status.terminated',
  transferred_out: 'pages.communicator.miniCard.status.transferredOut',
};

/**
 * Compact student context card for the communicator (spec 0009): who the
 * manager is talking to — groups, debt, financing flags, open tasks, notes.
 * Purely presentational; data comes from `useStudentMiniCard`. The finance
 * section and the per-group financing flags are null for viewers without
 * payment roles — null means "hidden", never "false", so flags render only
 * on `=== true` and the money section only when `finance` is present.
 */
export const StudentMiniCard = memo<IProps>(
  ({ studentId, card, loading, failed, onRetry }) => {
    const { t } = useTranslation();
    const history = useHistory();
    // HR видит студента в коммуникаторе, но маршрута карточки у него нет.
    const canOpenCard = useCanOpenStudentCard();
    // ТЗ 0104: ИИ-портрет — роли карточки студента (`AI_PORTRAIT_ROLES`).
    const { canSeePortrait } = useAiAgentPermissions();

    if (loading && !card) {
      return (
        <S.Card>
          <Skeleton active paragraph={{ rows: 5 }} title={false} />
        </S.Card>
      );
    }

    if (failed || !card) {
      return (
        <S.FailedWrap>
          {t('pages.communicator.loadFailed')}
          {onRetry ? (
            <S.RetryLink type="button" onClick={onRetry}>
              {t('pages.communicator.miniCard.retry')}
            </S.RetryLink>
          ) : null}
        </S.FailedWrap>
      );
    }

    const { finance } = card;
    const overflowTasks = card.openTasksCount - card.recentTasks.length;
    const overflowNotes = card.notesCount - card.recentNotes.length;

    return (
      <S.Card>
        <S.Header>
          <S.Name>{personName(card)}</S.Name>
          {card.isLmsBlocked && <LmsBlockBadge />}
        </S.Header>
        <S.Contact>
          {card.email}
          {card.phone ? ` · ${card.phone}` : ''}
        </S.Contact>

        <S.Section>
          <S.SectionTitle>
            {t('pages.communicator.miniCard.groupsTitle')}
          </S.SectionTitle>
          {card.groups.length === 0 && (
            <S.Muted>{t('pages.communicator.miniCard.groupsEmpty')}</S.Muted>
          )}
          {card.groups.map((row) => (
            <S.GroupRow key={row.agreementId}>
              <S.GroupName>{row.groupName}</S.GroupName>
              {row.courseName ? (
                <S.GroupCourse>{row.courseName}</S.GroupCourse>
              ) : null}
              <S.StatusTag tone={STATUS_TONE[row.status] || 'muted'}>
                {t(STATUS_KEY[row.status] || STATUS_KEY.active)}
                {row.status === 'terminated' && row.terminatedDate
                  ? ` · ${date(row.terminatedDate)}`
                  : ''}
              </S.StatusTag>
              {row.freeOfCharge === true && (
                <S.FlagTag>
                  {t('pages.communicator.miniCard.flags.freeOfCharge')}
                </S.FlagTag>
              )}
              {row.statePrefinanced === true && (
                <S.FlagTag>
                  {t('pages.communicator.miniCard.flags.statePrefinanced')}
                </S.FlagTag>
              )}
              {row.paymentByCompany === true && (
                <S.FlagTag>
                  {t('pages.communicator.miniCard.flags.paymentByCompany')}
                </S.FlagTag>
              )}
            </S.GroupRow>
          ))}
        </S.Section>

        {finance ? (
          <S.Section>
            <S.SectionTitle>
              {t('pages.communicator.miniCard.financeTitle')}
            </S.SectionTitle>
            {finance.isDebtor ? (
              <S.DebtBadge debtor>
                {t('pages.communicator.miniCard.debtor')} ·{' '}
                {/* Просрочка по валютам (`overdueAmounts`): одно число
                    складывало валюты и печаталось без кода валюты. */}
                {formatAmountsDue(finance.overdueAmounts) || money(0)} ·{' '}
                {t('pages.communicator.miniCard.overdueDays', {
                  n: finance.maxOverdueDays,
                })}
                {finance.overdueSince
                  ? ` (${t('pages.communicator.miniCard.overdueSince', {
                      date: date(finance.overdueSince),
                    })})`
                  : ''}{' '}
                ·{' '}
                {t('pages.communicator.miniCard.overdueInvoices', {
                  n: finance.overdueInvoiceCount,
                })}
              </S.DebtBadge>
            ) : (
              <S.DebtBadge debtor={false}>
                {t('pages.communicator.miniCard.noDebt')}
              </S.DebtBadge>
            )}
            {finance.totals.byCurrency.map((row, index) => (
              // Сервер может прислать две строки одной валюты — ключ по
              // валюте повторялся (предупреждение React). Порядок строк
              // задаёт сервер, индекс их не переставляет.
              // eslint-disable-next-line react/no-array-index-key
              <S.TotalsRow key={`${row.currency}-${index}`}>
                <S.TotalsCell>
                  {t('pages.communicator.miniCard.totalBudget')}:{' '}
                  {money(row.totalBudget)} {row.currency}
                </S.TotalsCell>
                <S.TotalsCell>
                  {t('pages.communicator.miniCard.totalPaid')}:{' '}
                  {money(row.totalPaid)}
                </S.TotalsCell>
                <S.TotalsCell danger={row.totalDebt > 0}>
                  {t('pages.communicator.miniCard.totalDebt')}:{' '}
                  {money(row.totalDebt)}
                </S.TotalsCell>
                {/* ТЗ 0036 — без «Предстоит» тройка бюджет/оплачено/долг
                    перестала сходиться ровно на величину будущего плана, а
                    именно эта карточка была примером противоречия в спеке. */}
                <S.TotalsCell>
                  {t('pages.communicator.miniCard.totalUpcoming')}:{' '}
                  {money(row.totalUpcoming)}
                </S.TotalsCell>
              </S.TotalsRow>
            ))}
          </S.Section>
        ) : null}

        {card.recentTasks.length > 0 && (
          <S.Section>
            <S.SectionTitle>
              {t('pages.communicator.miniCard.tasksTitle')} (
              {card.openTasksCount})
            </S.SectionTitle>
            {card.recentTasks.map((task) => (
              <S.ItemRow key={task.id}>
                <S.ItemTitle>{task.title}</S.ItemTitle>
                {task.dueDate ? (
                  <S.ItemMeta
                    overdue={moment(task.dueDate).isBefore(moment(), 'day')}
                  >
                    {date(task.dueDate)}
                  </S.ItemMeta>
                ) : null}
              </S.ItemRow>
            ))}
            {overflowTasks > 0 && (
              <S.MoreHint>
                {t('pages.communicator.miniCard.tasksMore', {
                  n: overflowTasks,
                })}
              </S.MoreHint>
            )}
          </S.Section>
        )}

        {card.recentNotes.length > 0 && (
          <S.Section>
            <S.SectionTitle>
              {t('pages.communicator.miniCard.notesTitle')} ({card.notesCount})
            </S.SectionTitle>
            {card.recentNotes.map((note) => (
              <div key={note.id}>
                <S.NoteMeta>
                  {note.pinned ? '📌 ' : ''}
                  {note.authorName || '—'} · {date(note.createdAt)}
                </S.NoteMeta>
                <S.NoteBody>{note.body}</S.NoteBody>
              </div>
            ))}
            {overflowNotes > 0 && (
              <S.MoreHint>
                {t('pages.communicator.miniCard.notesMore', {
                  n: overflowNotes,
                })}
              </S.MoreHint>
            )}
          </S.Section>
        )}

        {/* Spec 0038 — what this student has been saying about the course.
            `surveys` is NULL (not empty) for roles without survey access:
            the block quotes them verbatim and names the teacher, unlike the
            counts above it. Rendering nothing is the correct response to
            «скрыто от тебя». */}
        {card.surveys && (
          <S.Section>
            <S.SectionTitle>{t('pages.surveys.miniCard.title')}</S.SectionTitle>
            <SurveyCounts>
              <span>
                {t('pages.surveys.miniCard.answered', {
                  n: card.surveys.respondedCount,
                })}
              </span>
              {card.surveys.pendingCount > 0 && (
                <span>
                  {t('pages.surveys.miniCard.pending', {
                    n: card.surveys.pendingCount,
                  })}
                </span>
              )}
              {card.surveys.negativeCount > 0 && (
                <SurveyWarn>
                  {t('pages.surveys.miniCard.negative', {
                    n: card.surveys.negativeCount,
                  })}
                </SurveyWarn>
              )}
            </SurveyCounts>
            {card.surveys.recentSignals.map((signal) => (
              <SurveySignal
                key={signal.responseId}
                $tone={sentimentTone(signal.sentiment)}
              >
                <S.NoteMeta>
                  {signal.surveyTitle} · {signal.groupName}
                  {signal.lessonNumber ? ` · №${signal.lessonNumber}` : ''}
                  {signal.teacherName ? ` · ${signal.teacherName}` : ''}
                </S.NoteMeta>
                {signal.excerpt ? (
                  <S.NoteBody>«{signal.excerpt}»</S.NoteBody>
                ) : null}
              </SurveySignal>
            ))}
            {canOpenCard && card.surveys.respondedCount > 0 && (
              <S.MoreHint>
                <SurveyLink
                  type="button"
                  onClick={() =>
                    history.push(
                      `${PATHS.studentDetail.replace(
                        ':id',
                        studentId,
                      )}?tab=surveys`,
                    )
                  }
                >
                  {t('pages.surveys.miniCard.all')}
                </SurveyLink>
              </S.MoreHint>
            )}
          </S.Section>
        )}

        {/* ТЗ 0104, агент 3 — короткий ИИ-портрет: свой ленивый запрос,
            уходит только у открытой карточки (сюда рендер доходит лишь с
            загруженной `card`). Без права — ни раздела, ни запроса. */}
        {canSeePortrait ? (
          <MiniCardAiPortrait studentId={studentId} canOpenCard={canOpenCard} />
        ) : null}

        {canOpenCard ? (
          <S.Footer>
            <S.OpenLink
              type="button"
              onClick={() =>
                history.push(PATHS.studentDetail.replace(':id', studentId))
              }
            >
              {t('pages.communicator.miniCard.openCard')}
            </S.OpenLink>
          </S.Footer>
        ) : null}
      </S.Card>
    );
  },
);

StudentMiniCard.displayName = 'StudentMiniCard';

const TONE_COLOR: Record<string, string> = {
  weak: 'var(--k-fg-dc2626)',
  ok: 'var(--k-fg-d97706)',
  strong: 'var(--k-fg-16a34a)',
  unknown: 'var(--k-fg-d1d5db)',
};

const SurveyCounts = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  font-size: 12px;
  color: var(--k-fg-6b7280);
  margin-bottom: 4px;
`;

const SurveyWarn = styled.span`
  color: var(--k-fg-dc2626);
`;

const SurveySignal = styled.div<{ $tone: string }>`
  border-left: 2px solid
    ${({ $tone }) => TONE_COLOR[$tone] || TONE_COLOR.unknown};
  padding-left: 6px;
  margin-bottom: 4px;
`;

const SurveyLink = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  color: var(--k-fg-2563eb);
  cursor: pointer;
  font-size: 12px;
`;
